import { differenceInSeconds } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '~/components/Button';
import { showToast } from '~/components/Toast';
import { Toggle } from '~/components/Toggle';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useBrand } from '~/modules/branding';
import { DetailPageError } from '~/modules/errors/components/DetailPageError';
import { ErrorContent } from '~/modules/errors/components/ErrorContent';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';
import type { LabelDetail } from '~settings/api/lib/schemas';
import { HttpStatusCode } from '~settings/api/lib/types';
import { useLabelCreate } from '~settings/api/mutations/labelCreate';
import { useLabel } from '~settings/api/queries/label';
import { CTABlock } from '~settings/components/CTABlock';
import { SettingsPageHeader } from '~settings/components/SettingsPageHeader';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import type { LabelFormSchema } from '~settings/modules/label/components/LabelForm';
import { LabelForm } from '~settings/modules/label/components/LabelForm';
import {
  getErrorMessage,
  getLabelPayload,
  getLabelValues,
} from '~settings/modules/label/helpers';
import { isValidDateInsideTimeframe } from '~settings/modules/sublabel-request/helpers';
import type { LabelDetailTransformed } from '~settings/utils/transformers';
import { transformLabelDetails } from '~settings/utils/transformers';

const FORM_ID = 'createSublabelFromRequestForm';

export function CreateSublabelFromRequestPage() {
  const brand = useBrand();
  const { hasPermission, email } = useUser();

  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { trackEvent } = useAnalytics('ADA: User management');
  const pageOpenedAt = useRef<number>();

  const canCreateLabel = hasPermission('LABEL.CREATE', {
    id: brand.name,
    type: 'APPLICATION',
  });

  const parentId = searchParams.get('parentId') ?? '';
  const notes = searchParams.get('notes') ?? '';
  const name = searchParams.get('name') ?? '';

  const {
    isLoading,
    error,
    label: parentLabel,
  } = useLabel<LabelDetailTransformed>(parentId, {
    enabled: !!(parentId && name) && canCreateLabel,
    select: transformLabelDetails,
  });

  const { mutate: createLabel, isPending } = useLabelCreate({
    onSuccess: (data: LabelDetail) => {
      const createdAt = searchParams.get('createdAt') ?? '';

      const analyticsParams = {
        parent_label_name: parentLabel?.name ?? '',
        label_name: data.name,
        label_pmo: data.pmo ?? '',
        notes_present: notes.length > 0,
        approved_by: email,
        ...getTimeParams(createdAt, pageOpenedAt.current),
      };

      trackEvent('sublabelCreateApproveSublabelCreation', analyticsParams);

      navigate(SETTINGS_ROUTES.LABEL_DETAIL(data.id));
    },
    onError: async (err) => {
      const message = await getErrorMessage(err, t, true);

      showToast(message);
    },
  });

  useEffect(() => {
    pageOpenedAt.current = Date.now();
  }, []);

  const onSubmit = (data: LabelFormSchema) => {
    const payload = getLabelPayload(data, isActive);

    createLabel(payload);
  };

  if (!canCreateLabel) {
    return <ErrorContent type="noAccess" />;
  }

  const [isActive, setIsActive] = useState<boolean>(
    parentLabel?.active ?? true,
  );
  const labelValues = useMemo<LabelFormSchema | undefined>(
    () =>
      parentLabel
        ? getLabelValues({
            name: searchParams.get('name') ?? '',
            parentId: searchParams.get('parentId') ?? '',
            parentName: parentLabel.name,
            weaLabelCode: '',
            dealOwner: parentLabel.dealOwner,
            dealRegionScope: parentLabel.dealRegionScope,
            wmiLabelCode: parentLabel.wmiLabelCode,
            marketingCompany: parentLabel.marketingCompany || '',
            repertoireOwner: parentLabel.repertoireOwner ?? '',
            wbsElementNumber: parentLabel.wbsElementNumber ?? '',
            dealId: parentLabel.dealId || '',
            scopeId: parentLabel.scopeId || '',
            dealScope: parentLabel.dealScope || '',
            profitCenter: parentLabel.profitCenter || '',
            pmo: parentLabel.pmo || '',
            gvlCode: parentLabel.gvlCode ?? '',
            labelIsrcPrefix: parentLabel.labelIsrcPrefix ?? '',
            distributorId: parentLabel.distributorId ?? '',
            gpiNumber: parentLabel.gpiNumber ?? '',
          } as LabelDetailTransformed)
        : undefined,
    [parentLabel, searchParams],
  );

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  // temporary show error page if parent label is missing
  // as we should handle multiple invalid cases - no label name, label name is not unique, parent label not existing anymore etc
  if (error || !parentLabel || !labelValues || !parentId) {
    return (
      <DetailPageError
        errorCode={HttpStatusCode.NotFound}
        fallbackRoute={{
          href: SETTINGS_ROUTES.LABEL_LIST,
          label: t('backToUserManagement'),
        }}
      />
    );
  }

  return (
    <article className="relative flex min-h-full flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.LABEL_LIST, label: 'labels' },
          { to: '', label: 'label.createSublabelTitle' },
        ]}
        title="label.createSublabelTitle"
        actions={
          <Toggle
            pressed={isActive}
            onPressedChange={setIsActive}
            activeLabel={t('active')}
            inactiveLabel={t('inactive')}
          />
        }
      />
      <div className="grow overflow-y-auto pb-xl">
        <LabelForm
          onSubmit={onSubmit}
          values={labelValues}
          isActive={isActive}
          id={FORM_ID}
          notes={notes}
          isSublabelRequest={true}
        />
      </div>

      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.LABEL_LIST}>{t('buttons.cancel')}</Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          form={FORM_ID}
          size="large"
          disabled={isPending}
        >
          {t('label.addLink')}
        </Button>
      </CTABlock>
    </article>
  );
}

function getTimeParams(createdAt: string | null, pageOpenedAt?: number) {
  const now = Date.now();

  let requestCreationToApproval = 0;
  let labelCreateSecondsPassed = 0;

  if (pageOpenedAt) {
    // date is valid after feature implementation started and if less than now
    if (
      isValidDateInsideTimeframe(createdAt, '2026-01-01') &&
      +createdAt! < pageOpenedAt
    ) {
      requestCreationToApproval = differenceInSeconds(now, +createdAt!, {
        roundingMethod: 'ceil',
      });
    }

    labelCreateSecondsPassed = differenceInSeconds(now, pageOpenedAt, {
      roundingMethod: 'ceil',
    });
  }

  return {
    // time from sublabel request creation to sublabel creation
    from_request_creation_to_approval_seconds: requestCreationToApproval,
    // time from the moment the page is opened to the moment valid form submitted and label is created successfully
    time_to_create_label_seconds: labelCreateSecondsPassed,
    created_at: now,
  };
}

CreateSublabelFromRequestPage.displayName = 'CreateSublabelFromRequestPage';
